import { Box, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../components';
import CommonStatusChip from '../../components/ui-components/StatusChipComponent';
import { ContractApiResponse } from '../../services/contract-services';
import { formatToUSDate, formatCurrency } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';

import { OverViewWrapper, OverviewSectionCard, OverviewSectionHeader, OverviewNestedCard } from './ContractsStyles';

interface OverViewProps {
    contract?: ContractApiResponse;
}

interface MetricCardProps {
    label: React.ReactNode;
    value: React.ReactNode;
    highlight?: boolean;
}

// Shared label/value pair used throughout the overview sub-sections.
// `highlight` renders the tile in the errorPreset styling used for an amount that needs attention (e.g. Past Due).
const MetricCard: React.FC<MetricCardProps> = ({ label, value, highlight }) => (
    <Box
        sx={
            highlight
                ? {
                      backgroundColor: colors.errorPreset[20],
                      border: `1px solid ${colors.errorPreset[30]}`,
                      borderRadius: 1,
                      p: 1.5,
                  }
                : undefined
        }
    >
        <Typography variant="body2" mb={0.5} color={highlight ? colors.errorPreset[10] : colors.greyPreset[100]}>
            {label}
        </Typography>
        <Typography variant="subtitle1" color={highlight ? colors.errorPreset[10] : 'textPrimary'}>
            {value}
        </Typography>
    </Box>
);

interface MetricsGridProps {
    isFourColumns: boolean;
    children: React.ReactNode;
}

// Shared responsive grid wrapper for the metric tiles within a section.
const MetricsGrid: React.FC<MetricsGridProps> = ({ isFourColumns, children }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                lg: isFourColumns ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
            },
            columnGap: 2,
            rowGap: { xs: 2.5, lg: 2 },
        }}
    >
        {children}
    </Box>
);

interface SectionCardProps {
    title: React.ReactNode;
    action?: React.ReactNode;
    children: React.ReactNode;
}

// Shared white card wrapper with an uppercase section header, used to group the overview details.
// `action` renders an optional badge (e.g. "Action Required") on the right of the header.
const SectionCard: React.FC<SectionCardProps> = ({ title, action, children }) => (
    <OverviewSectionCard>
        <OverviewSectionHeader
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
        >
            <Typography variant="overline" color="textPrimary" fontWeight={700} letterSpacing={1}>
                {title}
            </Typography>
            {action}
        </OverviewSectionHeader>
        {children}
    </OverviewSectionCard>
);

const OverView: React.FC<OverViewProps> = ({ contract }) => {
    const { t } = useTranslation();

    if (!contract) {
        return (
            <Box bgcolor={colors.greyPreset[90]} borderRadius={4} p={2}>
                <Typography>No contract data available</Typography>
            </Box>
        );
    }
    // Format frequency
    const formatFrequency = (frequency: string): string => {
        const freqMap: { [key: string]: string } = {
            M: 'Monthly',
            W: 'Weekly',
            B: 'Bi-weekly',
            Q: 'Quarterly',
            Y: 'Yearly',
        };
        return freqMap[frequency] || frequency;
    };
    const hasPastDue = Number(contract.PastDue) > 0;
    return (
        <OverViewWrapper gap={3} mb={3} mx={3} p={{ xs: 1.5, sm: 2 }}>
            <SectionCard title="Schedule & Terms">
                <MetricsGrid isFourColumns={!!contract.IsLoan}>
                    {contract.IsLoan && (
                        <MetricCard
                            label={t('contracts.details.oldestOpenInvoiceDate')}
                            value={contract.CurrentPaymentDate && formatToUSDate(contract.CurrentPaymentDate)}
                        />
                    )}
                    <MetricCard
                        label={t('contracts.details.billingFrequency')}
                        value={contract.Frequency && formatFrequency(contract.Frequency)}
                    />
                    <MetricCard
                        label={t('contracts.details.startDate')}
                        value={contract.BookingDate && formatToUSDate(contract.BookingDate)}
                    />
                    <MetricCard
                        label={
                            contract.IsLoan ? t('contracts.details.maturityDate') : t('contracts.details.termEndDate')
                        }
                        value={contract.MaturityDate && formatToUSDate(contract.MaturityDate)}
                    />
                    {contract.IsLoan && (
                        <MetricCard
                            label={t('contracts.details.term')}
                            value={
                                <>
                                    {contract.Term} {contract.Term === 1 ? 'Month' : 'Months'}
                                </>
                            }
                        />
                    )}
                    <MetricCard
                        label="Last Payment Made"
                        value={contract.LastPaymentDate ? formatToUSDate(contract.LastPaymentDate) : 'N/A'}
                    />
                </MetricsGrid>
            </SectionCard>

            <SectionCard
                title="Balances & Billing"
                action={
                    hasPastDue && (
                        <CommonStatusChip
                            label="Action Required"
                            chipBgColor={colors.errorPreset[20]}
                            chipBorderColor={colors.errorPreset[30]}
                            chipTextColor={colors.errorPreset[10]}
                        />
                    )
                }
            >
                <MetricsGrid isFourColumns={!!contract.IsLoan}>
                    <MetricCard
                        label={t('contracts.details.nextPaymentDue')}
                        value={
                            contract.NextPaymentAmount != null &&
                            contract.NextPaymentAmount !== '' &&
                            formatCurrency(contract.NextPaymentAmount)
                        }
                    />
                    <MetricCard
                        label={t('contracts.details.pastDue')}
                        value={contract?.PastDue && formatCurrency(contract?.PastDue)}
                        highlight={hasPastDue}
                    />
                    <MetricCard
                        label={contract.IsLoan ? t('contracts.details.financedAmount') : 'Lease Value'}
                        value={contract.PrincipleAmount && formatCurrency(contract.PrincipleAmount)}
                    />
                    {contract.IsLoan && (
                        <MetricCard
                            label="Remaining Principal Amount"
                            value={contract.RemainingPrincipal && `${formatCurrency(contract.RemainingPrincipal)}`}
                        />
                    )}
                </MetricsGrid>
            </SectionCard>

            {contract.IsLoan && (
                <SectionCard title="Accounting Ledger Breakdown">
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr' },
                            gap: 2,
                        }}
                    >
                        <OverviewNestedCard>
                            <MetricCard
                                label="Life to Date Interest"
                                value={contract.TotalIntrestPaid && formatCurrency(contract.TotalIntrestPaid)}
                            />
                            <MetricCard
                                label="Life to Date Principal"
                                value={contract.TotalPrincipalPaid && formatCurrency(contract.TotalPrincipalPaid)}
                            />
                        </OverviewNestedCard>
                        <OverviewNestedCard>
                            <MetricCard
                                label="Year to Date Interest"
                                value={contract.YearToDateIntrest && formatCurrency(contract.YearToDateIntrest)}
                            />
                            <MetricCard
                                label="Year to Date Principal"
                                value={contract.YearToDatePrincipal && formatCurrency(contract.YearToDatePrincipal)}
                            />
                        </OverviewNestedCard>
                    </Box>
                    <OverviewNestedCard>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr' },
                                gap: 2,
                            }}
                        >
                            <MetricCard
                                label="Prior Year Interest Paid"
                                value={contract.PrevYearIntrest && formatCurrency(contract.PrevYearIntrest)}
                            />
                            <MetricCard
                                label="Prior Year Principal Paid"
                                value={contract.PrevYearPrincipal && formatCurrency(contract.PrevYearPrincipal)}
                            />
                        </Box>
                    </OverviewNestedCard>
                </SectionCard>
            )}
        </OverViewWrapper>
    );
};

export default OverView;
